import { useCallback, useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import CarouselMatch from "@/components/CarouselMatch";

export default function Carousel({ matches = [], className = "", autoplayDelay = 4000 }) {
  const autoplay = useRef(
    Autoplay({ delay: autoplayDelay, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      slidesToScroll: "auto",
      loop: true,
    },
    [autoplay.current]
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      const slidesToScroll = window.innerWidth >= 1024 ? 10 : 1;
      emblaApi.scrollPrev(false, slidesToScroll);
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      const slidesToScroll = window.innerWidth >= 1024 ? 10 : 1;
      emblaApi.scrollNext(false, slidesToScroll);
    }
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi) => {
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (emblaApi && autoplay.current) {
      autoplay.current.play();
    }
  }, [emblaApi]);

  if (!matches || matches.length === 0) {
    return null;
  }

  return (
    <>
      <style>{`
        .embla__viewport {
          overflow: hidden;
        }
        .embla__container {
          display: flex;
          gap: 0.75rem;
        }
        .embla__slide {
          flex: 0 0 calc((100% - 6.75rem) / 10);
          min-width: 300px;
        }
        @media (max-width: 1023px) {
          .embla__slide {
            flex: 0 0 300px;
            min-width: 300px;
          }
        }
      `}</style>
      <div className={`relative ${className}`}>
        {canScrollPrev && (
          <button
            onClick={scrollPrev}
            className="hidden @md:block absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all"
            aria-label="Scroll left"
          >
            <ChevronLeftIcon className="w-6 h-6 text-gray-700" />
          </button>
        )}

        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {matches.map((match) => (
              <div key={match.id} className="embla__slide flex-shrink-0 py-0.5">
                <CarouselMatch match={match} />
              </div>
            ))}
          </div>
        </div>

        {canScrollNext && (
          <button
            onClick={scrollNext}
            className="hidden @md:block absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all"
            aria-label="Scroll right"
          >
            <ChevronRightIcon className="w-6 h-6 text-gray-700" />
          </button>
        )}
      </div>
    </>
  );
}
